import type { JSX } from 'react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import { PlusOutlined, ShopOutlined } from '@ant-design/icons';

import { Panel, Tag } from '../ds';
import type { MyRoomDto } from '../../lib/api/openingTasks';
import { RoomDetail } from './RoomDetail';
import { fmtDate, fmtWhen, isRoomRejected, roomStageColor, roomStageLabel } from './utils';

/**
 * «Мои помещения»: карточки заявок «Осмотр помещения». Нажатие на карточку
 * открывает помещение в большом окне поверх экрана — переписка, фото, видео,
 * планировки без прокрутки вниз (владелец 07.10.2026). Отказанные («Не
 * подходит») в список не попадают. Выбор живёт в адресе — `room=<submissionId>`
 * (ссылки из уведомлений открывают нужное): в визарде «Открытие точки» это
 * `/opening?tab=appeals&room=…` (остальные параметры, в том числе вкладка,
 * сохраняются), у партнёра без карточки открытия — `/tasks?room=…`.
 */
export function RoomsPanel({ rooms: allRooms }: { rooms: MyRoomDto[] }): JSX.Element {
  const rooms = allRooms.filter((r) => !isRoomRejected(r.stageCode, r.status));
  const [sp, setSp] = useSearchParams();
  const roomParam = sp.get('room');
  const selectedId = roomParam;
  // Ссылка из уведомления может вести и на отказанное помещение — окно откроется,
  // хотя карточки в списке нет.
  const selected = selectedId ? allRooms.find((r) => r.id === selectedId) ?? null : null;
  const [isPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 680px)').matches);

  const setRoom = (id: string | null): void => {
    const next = new URLSearchParams(sp);
    if (id) next.set('room', id);
    else next.delete('room');
    setSp(next, { replace: true });
  };

  return (
    <div id="fk-ot-rooms" style={{ scrollMarginTop: 76, minWidth: 0 }}>
      <Panel pad={0} icon={<ShopOutlined />} title="Мои помещения" sub="заявки «Осмотр помещения» — одна переписка с куратором">
        <div className="fk-ot-pbody">
          <div className="fk-ot-rooms">
            {rooms.map((r) => (
              <RoomCard key={r.id} room={r} active={r.id === selectedId} onClick={() => setRoom(r.id)} />
            ))}
            <Link to="/opening/room-check" className="fk-ot-room-add">
              <PlusOutlined />
              <span>Отправить новое помещение</span>
            </Link>
          </div>
          {rooms.length ? (
            <div className="fk-ot-note">Нажмите на помещение — откроется окно: переписка с куратором, фото, видео, планировки и моменты.</div>
          ) : (
            <div className="fk-ot-note">Помещений пока нет. Нашли вариант — отправьте его на осмотр, куратор ответит здесь же.</div>
          )}
        </div>
      </Panel>
      <Modal
        open={!!selectedId}
        onCancel={() => setRoom(null)}
        footer={null}
        closable={false}
        destroyOnHidden
        centered={!isPhone}
        width={isPhone ? '100vw' : 'min(1100px, 96vw)'}
        style={isPhone ? { top: 0, margin: 0, padding: 0, maxWidth: '100vw' } : undefined}
        styles={{ body: { maxHeight: isPhone ? 'calc(100dvh - 48px)' : 'calc(100vh - 96px)', overflowY: 'auto' } }}
        aria-label="Помещение"
      >
        {selectedId ? <RoomDetail key={selectedId} roomId={selectedId} room={selected} onClose={() => setRoom(null)} /> : null}
      </Modal>
    </div>
  );
}

function RoomCard({ room: r, active, onClick }: { room: MyRoomDto; active: boolean; onClick: () => void }): JSX.Element {
  const label = roomStageLabel(r.stageCode, r.stageLabel, r.status);
  return (
    <button type="button" className="fk-ot-room" aria-pressed={active} onClick={onClick}>
      {r.unread > 0 ? (
        <span className="fk-ot-badge fk-ot-room-unread" title="Новые сообщения куратора" aria-label={`Новых сообщений: ${r.unread}`}>
          {r.unread}
        </span>
      ) : null}
      <span className="fk-ot-room-top">
        <Tag color={roomStageColor(r.stageCode, r.status)}>{label}</Tag>
        {r.score != null ? (
          <span className="fk-ot-mono fk-ot-muted" style={{ fontSize: 12 }} title="Балл осмотра">
            {Math.round(r.score)}%
          </span>
        ) : null}
      </span>
      <span className="fk-ot-room-addr">{r.address || 'Адрес не указан'}</span>
      {r.openMoments > 0 || r.doneMoments > 0 ? (
        <span className="fk-ot-room-meta">
          {r.openMoments > 0 ? (
            <Tag color="orange" borderless>
              исправить: {r.openMoments}
            </Tag>
          ) : null}
          {r.doneMoments > 0 ? (
            <Tag color="gold" borderless>
              ждёт куратора: {r.doneMoments}
            </Tag>
          ) : null}
        </span>
      ) : null}
      <span className="fk-ot-lsub">
        {r.lastMessageAt ? `последнее сообщение ${fmtWhen(r.lastMessageAt)}` : `отправлено ${fmtDate(r.createdAt)}`}
      </span>
    </button>
  );
}
